import { InfoOutlined } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom'

import { Page } from '../../components'
import { CustomerLookupInput } from '../../components/CustomerLookUp'
import { CustomersByVehicleAutocomplete } from '../../components/CustomersByVehicleAutocomplete/CustomersByVehicleAutocomplete'
import RepairOrderCreationInfoPanel from '../../components/SharedUI/CustomerForms/RepairOrderCreationInfoPanel'
import { VehicleLookupInput } from '../../components/VehicleLookupInput'
import { VehiclesByCustomerAutocomplete } from '../../components/VehiclesByCustomerAutocomplete/VehiclesByCustomerAutocomplete'
import { sdk } from '../../config'
import { AppRoutes } from '../../models'
import { FeatureFlags } from '../../models/FeatureFlags'
import { QueryParamNames } from '../../models/QueryParamNames'
import { RO_CHECKED_IN } from '../../models/Status'
import { AuthContext } from '../../providers'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import {
  useCreateAuthorizationMutation,
  useCreateRepairOrderMutation,
  useGetAppointmentsQuery,
  useGetCustomersQuery,
  useGetUsersWithPermissionQuery,
  useGetVehicleDetailQuery
} from '../../redux/api'
import { useCreateServiceFromCannedJobMutation } from '../../redux/api/cannedJobApi'
import {
  useGetIntakeRequestsQuery,
  useUpdateIntakeRequestMutation
} from '../../redux/api/customerFormsApi'
import {
  AuthorizationInfoViaEnum,
  CustomerEntry,
  GetUsersWithPermissionActEnum,
  VehicleEntry
} from '../../sdk'
import { loggingAssert } from '../../utils/handleError/loggingAssert'

export default function NewEstimate() {
  const { currentUser, userShopId } = useContext(AuthContext)
  const accessToken = loggingAssert('Token not set', currentUser?.accessToken)
  const shopId = loggingAssert('ShopId not set', userShopId)
  const { shopProfile } = useContext(AppContext)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const paramCustomerId = searchParams.get(QueryParamNames.CUSTOMER_ID)
  const paramVehicleId = searchParams.get(QueryParamNames.VEHICLE_ID)
  const paramApptId = searchParams.get(QueryParamNames.APPT_ID)
  const isRepairOrder = searchParams.get(QueryParamNames.IS_REPAIR_ORDER)
  const paramIntakeRequestId = searchParams.get(
    QueryParamNames.INTAKE_REQUEST_ID
  )

  const { featureFlags } = useContext(AppContext)
  const isReadOnly = featureFlags && featureFlags[FeatureFlags.ReadOnly]

  const [newCustomer, setNewCustomer] = useState<CustomerEntry>()
  const [newVehicle, setNewVehicle] = useState<VehicleEntry>()
  const [checked, setChecked] = useState(!!isRepairOrder)
  const [includeCannedJobs, setIncludeCannedJobs] = useState<boolean>(true)
  const [authorizeCannedJobs, setAuthorizeCannedJobs] = useState<boolean>(false)

  // Get intake request by ID if one exists in the query string
  const { data: paramIntakeRequests } = useGetIntakeRequestsQuery(
    {
      authorization: accessToken,
      intakeRequestId: paramIntakeRequestId!
    },
    { skip: !paramIntakeRequestId }
  )
  const paramIntakeRequest = paramIntakeRequests && paramIntakeRequests[0]
  const initialCustomerId = paramIntakeRequestId
    ? paramIntakeRequest && paramIntakeRequest.entry!.info!.customerId
    : paramCustomerId
  const initialVehicleId = paramIntakeRequestId
    ? paramIntakeRequest && paramIntakeRequest.entry!.info!.vehicleId
    : paramVehicleId

  // Get most recent non-archived intake request if no intake request ID is in the query string
  const selectedCustomerId = newCustomer?.id
  const { data: customerIntakeRequests } = useGetIntakeRequestsQuery(
    {
      authorization: accessToken,
      customerId: selectedCustomerId!
    },
    { skip: !selectedCustomerId }
  )
  const latestIntakeRequestForCustomer = useMemo(() => {
    if (!customerIntakeRequests || customerIntakeRequests.length === 0) {
      return null
    }
    return customerIntakeRequests
      .filter((expanded) => !expanded.entry!.info!.repairOrderId)
      .sort(
        (a, b) =>
          b.entry!.info!.dateTime!.getTime() -
          a.entry!.info!.dateTime!.getTime()
      )[0]
  }, [customerIntakeRequests])

  // Prioritize intake requests specified by intake request ID
  const intakeRequest = paramIntakeRequestId
    ? paramIntakeRequest
    : latestIntakeRequestForCustomer

  const { data: advisors } = useGetUsersWithPermissionQuery({
    authorization: accessToken,
    shop: shopId,
    act: GetUsersWithPermissionActEnum.AssignToRepairOrder
  })
  const { data: customers } = useGetCustomersQuery(
    {
      authorization: accessToken,
      shop: userShopId!,
      customer: [initialCustomerId || '']
    },
    { skip: !initialCustomerId }
  )
  const { data: vehicles } = useGetVehicleDetailQuery(
    {
      authorization: accessToken,
      shop: shopId,
      vehicle: [initialVehicleId || '']
    },
    { skip: !initialVehicleId }
  )

  const { data: appointments } = useGetAppointmentsQuery(
    {
      authorization: accessToken,
      shop: shopId,
      appointment: [paramApptId || '']
    },
    { skip: !paramApptId }
  )

  const [createRepairOrder] = useCreateRepairOrderMutation()
  const [concern, setConcern] = useState<string>('')

  useEffect(() => {
    if (customers) {
      setNewCustomer(customers[0])
    }
  }, [customers])

  useEffect(() => {
    if (vehicles) {
      setNewVehicle(vehicles[0])
    }
  }, [vehicles])

  useEffect(() => {
    if (paramIntakeRequest && paramIntakeRequest.entry!.info!.concern) {
      setConcern(paramIntakeRequest.entry!.info!.concern)
    }
  }, [paramIntakeRequest])

  useEffect(() => {
    if (appointments) {
      const appointment = appointments && appointments[0]
      if (appointment) {
        setConcern(appointment.info.concern || '')
      }
    }
  }, [appointments])

  const isValidConcern =
    shopProfile &&
    (shopProfile!.info!.isOptionalConcern!
      ? concern.length === 0 || concern.length >= 3
      : concern.length >= 3)

  const [createCannedJobService] = useCreateServiceFromCannedJobMutation()
  const createCannedJobs: (repairOrderId: string) => Promise<void> = async (
    repairOrderId
  ) => {
    if (!intakeRequest?.cannedJobs?.length) {
      return
    }
    const expandedEntries = await sdk.getCannedJobExpanded({
      authorization: accessToken,
      shop: shopId,
      cannedJobs: intakeRequest!.cannedJobs!
    })
    for (const expandedEntry of expandedEntries) {
      await createCannedJobService({
        authorization: accessToken,
        createCannedJobService: {
          repairOrder: repairOrderId,
          cannedJobExpanded: expandedEntry
        }
      })
    }
  }

  const [updateIntakeRequest] = useUpdateIntakeRequestMutation()
  const [createAuthorization] = useCreateAuthorizationMutation()

  const handleSubmit = async () => {
    if (!isValidConcern) {
      return
    }

    if (newCustomer && newVehicle && advisors && currentUser) {
      const createdRepairOrder = await createRepairOrder({
        authorization: accessToken,
        repairOrderInfo: {
          shop: userShopId,
          customer: newCustomer.id!,
          vehicle: newVehicle.id!,
          isEstimate: !checked,
          concern: concern?.length >= 3 ? concern : undefined,
          appointment: paramApptId || undefined,
          status: checked ? RO_CHECKED_IN : undefined,
          serviceAdvisor: advisors
            .map((advisor) => advisor.id!)
            .includes(currentUser.id!)
            ? currentUser.id!
            : undefined
        }
      }).unwrap()
      if (intakeRequest && !intakeRequest.entry!.info!.repairOrderId) {
        if (includeCannedJobs) {
          await createCannedJobs(createdRepairOrder.id!)
          if (authorizeCannedJobs) {
            await createAuthorization({
              authorization: accessToken,
              authorizationInfo: {
                shop: shopId,
                repairOrder: createdRepairOrder.id!,
                dateTime: new Date(),
                amount: 0,
                via: AuthorizationInfoViaEnum.CustomerIntake,
                authorizer: intakeRequest.entry!.info!.customerId!
              }
            })
          }
        }
        updateIntakeRequest({
          authorization: accessToken,
          updateIntakeRequestObject: {
            intakeRequestId: intakeRequest!.entry!.id!,
            repairOrderId: createdRepairOrder.id!
          }
        })
      }
      navigate(
        generatePath(AppRoutes.ESTIMATE_DETAILS, {
          id: createdRepairOrder.id!
        })
      )
    }
  }

  const handleCustomerChange = useCallback(
    (customer: CustomerEntry | undefined) => {
      setNewCustomer(customer)
      setNewVehicle(undefined)
    },
    []
  )

  const handleVehicleChange = useCallback(
    (vehicle: VehicleEntry | undefined) => {
      setNewVehicle(vehicle)
      setNewCustomer(undefined)
    },
    []
  )

  return (
    <Page title={'New Estimate'}>
      <Card
        variant="outlined"
        sx={{
          width: '20rem',
          height: '100%',
          mx: 'auto',
          mt: '5rem'
        }}>
        {isReadOnly && (
          <Alert severity="warning" variant="filled">
            READ ONLY
          </Alert>
        )}

        <Stack direction="column" spacing={2} p={2}>
          <Typography variant="h3">
            Create a New {checked ? 'Repair Order' : 'Estimate'}
          </Typography>
          <Divider />
          <Stack
            direction={'row'}
            spacing={1}
            alignItems={'center'}
            justifyContent={'space-between'}>
            <Box sx={{ width: '100%' }}>
              {newVehicle ? (
                <CustomersByVehicleAutocomplete
                  vehicleId={newVehicle.id!}
                  customer={newCustomer}
                  setCustomer={setNewCustomer}
                  readOnly={!!(customers && customers[0])}
                  autoFocus={true}
                />
              ) : (
                <CustomerLookupInput
                  customer={newCustomer}
                  handleCustomerChange={handleCustomerChange}
                  readOnly={!!(customers && customers[0])}
                  autoFocus={true}
                />
              )}
            </Box>

            {newCustomer && newCustomer.information?.notes && (
              <Tooltip title={newCustomer.information!.notes || ''}>
                <InfoOutlined />
              </Tooltip>
            )}
          </Stack>

          {newCustomer ? (
            <VehiclesByCustomerAutocomplete
              customer={newCustomer}
              vehicle={newVehicle}
              setVehicle={setNewVehicle}
              readOnly={!!(vehicles && vehicles[0])}
              autoFocus={true}
              setCustomer={setNewCustomer}
            />
          ) : (
            <VehicleLookupInput
              vehicle={newVehicle}
              handleVehicleChange={handleVehicleChange}
              readOnly={!!(vehicles && vehicles[0])}
              autoFocus={false}
            />
          )}

          <TextField
            data-testid="EstimatesConcernField"
            label="Concern"
            value={concern}
            multiline
            minRows={3}
            onChange={({ target }) => setConcern(target.value)}
            placeholder="Minimum 3 characters"
            required={!shopProfile?.info?.isOptionalConcern}
            fullWidth
            error={concern.length > 0 && concern.length < 3}
            helperText={
              concern.length > 0 && concern.length < 3
                ? 'Concern must be at least 3 characters long.'
                : ''
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                disabled={!!isRepairOrder}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setChecked(event.target.checked)
                }}
              />
            }
            label="Checked In"
          />
          <Button
            variant="outlined"
            onClick={handleSubmit}
            disabled={
              !isValidConcern || !newCustomer || !newVehicle || isReadOnly
            }
            aria-label="createEstimateButton">
            Create
          </Button>
          {(!isValidConcern || !newCustomer || !newVehicle) && (
            <FormHelperText>
              Please fill out concern, customer and vehicle
            </FormHelperText>
          )}
          {intakeRequest && (
            <RepairOrderCreationInfoPanel
              showDismiss={intakeRequest !== paramIntakeRequest}
              intakeRequest={intakeRequest}
              includeCannedJobs={includeCannedJobs}
              setIncludeCannedJobs={setIncludeCannedJobs}
              authorizeCannedJobs={authorizeCannedJobs}
              setAuthorizeCannedJobs={setAuthorizeCannedJobs}
            />
          )}
        </Stack>
      </Card>
    </Page>
  )
}
